import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { PremiseConfig } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { usePremise } from "../hooks/usePremise";
import { useScope } from "./ScopeContext";

/**
 * The premise currently being worked on, loaded from the header's Location
 * scope rather than from the URL.
 *
 * This is the hinge of the estate-first IA: pages no longer live inside one
 * premise's editor, they live under a product area and read whichever premise
 * the header points at. Every panel that used to take `premise` / `onChange`
 * from PremiseEditorPage now takes them from here, unchanged.
 */
export interface PremiseWorkspaceState {
  /** Slug in scope, or "" when the header is set to all locations. */
  premiseId: string;
  /** The editable copy. Null while loading, or when nothing is scoped. */
  draft: PremiseConfig | null;
  setDraft: React.Dispatch<React.SetStateAction<PremiseConfig | null>>;
  loading: boolean;
  error: Error | null;
  dirty: boolean;
  saving: boolean;
  save: () => Promise<void>;
  /** Persist a specific config immediately - for controls that own their own
   *  write (product toggles) rather than feeding the shared draft. */
  saveNow: (next: PremiseConfig) => Promise<void>;
  /** Set by save(); the shell surfaces it. */
  lastResult: { msg: string; severity: "success" | "error" } | null;
  clearResult: () => void;
}

const Ctx = createContext<PremiseWorkspaceState | null>(null);

function deepEqual<T>(a: T, b: T): boolean {
  return JSON.stringify(a) === JSON.stringify(b);
}

export function PremiseWorkspaceProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { scope } = useScope();
  const premiseId = scope.premiseId;
  const { config, loading, error, applySaved } = usePremise(premiseId);
  const [draft, setDraft] = useState<PremiseConfig | null>(null);
  const [saving, setSaving] = useState(false);
  const [lastResult, setLastResult] = useState<{
    msg: string;
    severity: "success" | "error";
  } | null>(null);

  // Switching location throws the draft away. Carrying unsaved edits across
  // premises would be a data-loss bug, not a convenience.
  useEffect(() => {
    setDraft(null);
  }, [premiseId]);

  useEffect(() => {
    if (config) setDraft(config);
  }, [config]);

  const dirty = useMemo(
    () => !!draft && !!config && !deepEqual(draft, config),
    [draft, config]
  );

  const save = useCallback(async () => {
    if (!draft || !premiseId) return;
    setSaving(true);
    try {
      const stored = await foyerApi.putPremise(premiseId, draft);
      setDraft(stored);
      applySaved(stored);
      setLastResult({ msg: `Saved · v${stored.version}`, severity: "success" });
    } catch (err) {
      setLastResult({
        msg: err instanceof Error ? err.message : String(err),
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  }, [draft, premiseId, applySaved]);

  const saveNow = useCallback(
    async (next: PremiseConfig) => {
      if (!premiseId) return;
      setSaving(true);
      try {
        const stored = await foyerApi.putPremise(premiseId, next);
        setDraft(stored);
        applySaved(stored);
      } finally {
        setSaving(false);
      }
    },
    [premiseId, applySaved]
  );

  const value = useMemo<PremiseWorkspaceState>(
    () => ({
      premiseId,
      draft: premiseId ? draft : null,
      setDraft,
      loading: !!premiseId && loading,
      error,
      dirty,
      saving,
      save,
      saveNow,
      lastResult,
      clearResult: () => setLastResult(null),
    }),
    [premiseId, draft, loading, error, dirty, saving, save, saveNow, lastResult]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePremiseWorkspace(): PremiseWorkspaceState {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("usePremiseWorkspace must be used inside the CMS shell");
  }
  return ctx;
}
